import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Wrench, AlertTriangle } from "lucide-react";
import { useCanDelete } from "@/context/AuthContext";

const EMPTY = {
  bowser_id: "", service_type: "routine_service", description: "",
  cost: 0, date: new Date().toISOString().slice(0, 10),
  odometer_km: 0, vendor: "", invoice_no: "",
  next_due_date: "", next_due_km: 0, downtime_hours: 0,
  status: "completed", notes: "",
};

const SERVICE_TYPES = [
  { v: "routine_service", l: "Routine service" },
  { v: "breakdown", l: "Breakdown / Repair" },
  { v: "tyre", l: "Tyre change" },
  { v: "battery", l: "Battery" },
  { v: "brake", l: "Brake" },
  { v: "oil_change", l: "Oil change" },
  { v: "inspection", l: "Inspection" },
  { v: "repair", l: "Other repair" },
];

const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");

export default function ServiceRecords() {
  const qc = useQueryClient();
  const canDelete = useCanDelete();
  const { data: bowsersData } = useQuery({ queryKey: ["fleet-bowsers-full"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data) });
  const bowsers = bowsersData?.items || [];

  const [filterBowser, setFilterBowser] = useState("");
  const [filterType, setFilterType] = useState("");
  const { data } = useQuery({
    queryKey: ["maintenance", filterBowser, filterType],
    queryFn: () => api.get(`/fleet/maintenance?bowser_id=${filterBowser}&service_type=${filterType}`).then((r) => r.data),
  });

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });

  const save = useMutation({
    mutationFn: () => (editingId ? api.patch(`/fleet/maintenance/${editingId}`, form) : api.post("/fleet/maintenance", form)),
    onSuccess: () => { toast.success(editingId ? "Record updated" : "Record added"); qc.invalidateQueries({ queryKey: ["maintenance"] }); qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] }); qc.invalidateQueries({ queryKey: ["service-due"] }); setOpen(false); setEditingId(null); setForm(EMPTY); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/fleet/maintenance/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["maintenance"] }); qc.invalidateQueries({ queryKey: ["service-due"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (m) => { setForm({ ...EMPTY, ...m, date: (m.date || "").slice(0, 10), next_due_date: (m.next_due_date || "").slice(0, 10) }); setEditingId(m.id); setOpen(true); };

  const totalCost = (data?.items || []).reduce((s, m) => s + (m.cost || 0), 0);
  const totalDowntime = (data?.items || []).reduce((s, m) => s + (m.downtime_hours || 0), 0);
  const openBreakdowns = (data?.items || []).filter((m) => m.service_type === "breakdown" && m.status !== "completed").length;

  return (
    <div className="space-y-4" data-testid="service-records-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Bowser Service Records</h1>
        <p className="text-sm text-muted-foreground">Individual log of routine servicing, breakdowns, tyre replacement, battery changes, oil changes and other repairs. Each entry updates the bowser&rsquo;s service-due schedule.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard label="Records" value={data?.items?.length || 0} />
        <KpiCard label="Total spend" value={money(totalCost)} />
        <KpiCard label="Open breakdowns" value={openBreakdowns} icon={AlertTriangle} highlight={openBreakdowns > 0} />
        <KpiCard label="Downtime (hrs)" value={totalDowntime.toFixed(1)} />
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div className="flex flex-1 items-center gap-2">
            <Select value={filterBowser || "all"} onValueChange={(v) => setFilterBowser(v === "all" ? "" : v)}>
              <SelectTrigger className="max-w-52" data-testid="filter-bowser"><SelectValue placeholder="All bowsers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bowsers</SelectItem>
                {bowsers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.registration_no}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
              <SelectTrigger className="max-w-52" data-testid="filter-type"><SelectValue placeholder="All service types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All service types</SelectItem>
                {SERVICE_TYPES.map((s) => (<SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="btn-new-service"><Plus size={14} className="mr-1" /> Add record</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editingId ? "Edit service record" : "Add service / breakdown record"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Bowser</Label>
                  <Select value={form.bowser_id} onValueChange={upd("bowser_id")}>
                    <SelectTrigger data-testid="svc-bowser"><SelectValue placeholder="Select bowser" /></SelectTrigger>
                    <SelectContent>
                      {bowsers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.registration_no}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service type</Label>
                  <Select value={form.service_type} onValueChange={upd("service_type")}>
                    <SelectTrigger data-testid="svc-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICE_TYPES.map((s) => (<SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={upd("description")} placeholder="What was done?" data-testid="svc-desc" /></div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={upd("date")} data-testid="svc-date" /></div>
                <div><Label>Cost (₹)</Label><Input type="number" step="0.01" value={form.cost} onChange={upd("cost")} data-testid="svc-cost" /></div>
                <div><Label>Odometer @ service (km)</Label><Input type="number" value={form.odometer_km} onChange={upd("odometer_km")} data-testid="svc-odo" /></div>
                <div><Label>Downtime (hours)</Label><Input type="number" step="0.5" value={form.downtime_hours} onChange={upd("downtime_hours")} /></div>
                <div><Label>Vendor / Workshop</Label><Input value={form.vendor} onChange={upd("vendor")} placeholder="MRF Service Point" /></div>
                <div><Label>Invoice / bill no</Label><Input value={form.invoice_no} onChange={upd("invoice_no")} /></div>
                <div><Label>Next due date</Label><Input type="date" value={form.next_due_date} onChange={upd("next_due_date")} data-testid="svc-due-date" /></div>
                <div><Label>Next due odometer (km)</Label><Input type="number" value={form.next_due_km} onChange={upd("next_due_km")} data-testid="svc-due-km" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={upd("status")}>
                    <SelectTrigger data-testid="svc-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={upd("notes")} /></div>
              </div>
              <DialogFooter><Button onClick={() => save.mutate()} disabled={!form.bowser_id || !form.description || save.isPending} data-testid="btn-save-service">{save.isPending ? "Saving…" : "Save"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Bowser</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-right">Odo (km)</th>
                <th className="px-4 py-2 text-right">Cost</th>
                <th className="px-4 py-2">Next due</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((m) => (
                <tr key={m.id} className="border-b border-border/40 text-sm" data-testid={`svc-row-${m.id}`}>
                  <td className="px-4 py-2 font-mono">{m.bowser_code}</td>
                  <td className="px-4 py-2 text-xs">{m.date?.slice(0, 10)}</td>
                  <td className="px-4 py-2 text-xs uppercase">
                    {m.service_type === "breakdown"
                      ? <span className="rounded-full bg-status-cancelled/10 px-2 py-0.5 font-semibold text-status-cancelled">Breakdown</span>
                      : m.service_type.replace(/_/g, " ")}
                  </td>
                  <td className="px-4 py-2 text-xs max-w-64 truncate" title={m.description}>{m.description}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{m.odometer_km ? m.odometer_km.toLocaleString() : "—"}</td>
                  <td className="px-4 py-2 text-right font-mono">{money(m.cost)}</td>
                  <td className="px-4 py-2 text-xs">
                    {m.next_due_date && <div>{m.next_due_date.slice(0, 10)}</div>}
                    {m.next_due_km ? <div className="font-mono text-muted-foreground">{m.next_due_km.toLocaleString()} km</div> : null}
                    {!m.next_due_date && !m.next_due_km && "—"}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`rounded-full px-2 py-0.5 font-semibold uppercase ${m.status === "completed" ? "bg-status-delivered/10 text-status-delivered" : m.status === "in_progress" ? "bg-status-pending/10 text-status-pending" : "bg-muted"}`}>
                      {m.status?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`svc-edit-${m.id}`}><Pencil size={12} /></Button>
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`svc-del-${m.id}`}><Trash2 size={12} /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete this record?</AlertDialogTitle><AlertDialogDescription>Cannot be undone. Only admins can delete service records.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del.mutate(m.id)} data-testid={`svc-del-confirm-${m.id}`}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  <Wrench size={32} className="mx-auto mb-2 opacity-40" />
                  No service records. Log routine servicing, breakdowns, tyre replacements etc. to enable service-due alerts.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const KpiCard = ({ label, value, icon: Icon, highlight }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      {Icon && <Icon size={14} />}
    </div>
    <div className={`mt-2 font-display text-2xl font-black ${highlight ? "text-status-cancelled" : ""}`}>{value}</div>
  </div>
);
